import {
  listPersonioSyncLogsQuery,
  retrievePersonioSyncLogQuery,
  triggerPersonioPayrollSyncMutation
} from '../../../../../queries/company/personio';
import {
  listPaychexPayrollSyncsQuery,
  retrievePaychexPayrollSyncQuery,
  triggerPaychexPayrollSyncMutation
} from '../../../../../queries/company/paychex';
import { useStore } from '../../../../../hooks/useStore';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Skeleton,
  List,
  ListItem,
  LinearProgress,
  Button,
  Alert
} from '@mui/material';
import { format, formatRelative, getTime, parseISO } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import Util from '../../../../../misc/util';
import {
  createColumnHelper,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  Row,
  useReactTable
} from '@tanstack/react-table';
import { Fragment, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  PersonioSync,
  PaychexPayrollSync,
  MonthField,
  ProcessingStateEnum
} from '@pxtra-client-lib-legacy/gen';
import { ArrowRightIcon, ArrowUpIcon } from '../../../../../lib/icons';
import { StatusBadge } from '../../../../../lib/components/atomic/dataDisplay/statusBadge';

interface Event {
  id: string;
  timestamp: string;
  message: string;
}

type SyncLog = PersonioSync | PaychexPayrollSync;
type SyncType = 'personio' | 'paychex';

const SYNC_STATE_MODE: Record<ProcessingStateEnum, 'error' | 'info' | 'success' | 'warning'> = {
  CREATED: 'info',
  PROCESSING: 'info',
  DONE: 'success',
  ERROR: 'error'
};

const SYNC_ERROR_MESSAGE_KEY: Record<string, string> = {
  sync_already_running: 'tasks:SYNC_ALREADY_RUNNING',
  invalid_period: 'tasks:SYNC_INVALID_PERIOD',
  credentials_missing: 'tasks:SYNC_CREDENTIALS_MISSING',
  paychex_not_enabled: 'tasks:SYNC_NOT_ENABLED'
};

const resolveSyncErrorKey = (error: unknown): string => {
  const code = (error as { body?: { code?: string } })?.body?.code;
  return (code && SYNC_ERROR_MESSAGE_KEY[code]) || 'tasks:SYNC_ERROR';
};

interface EventLogsComponentProps {
  row: Row<SyncLog>;
  type: SyncType;
}

function monthFieldToString(month: MonthField | null | undefined): string {
  if (!month) return '';
  return `${month.year}-${String(month.month).padStart(2, '0')}`;
}

function EventLogsComponent({ row, type }: EventLogsComponentProps) {
  const { i18n, t } = useTranslation();
  const { companyStore } = useStore();
  const queryClient = useQueryClient();
  const companyId =
    type === 'paychex' ? (companyStore.company?.id ?? '') : (row.original.company ?? '');
  const { data, isLoading } =
    type === 'personio'
      ? retrievePersonioSyncLogQuery(companyId, row.original.id)
      : retrievePaychexPayrollSyncQuery(companyId, row.original.id);

  // Refresh the list (and this detail) so the new PROCESSING state shows up and
  // interval polling starts.
  const invalidateSyncQueries = () =>
    queryClient.invalidateQueries({ queryKey: ['company', companyId, type, 'sync'] });

  const accountingPeriodString = monthFieldToString(
    (row.original as PaychexPayrollSync).accounting_period
  );
  const {
    mutate: triggerPaychexPayrollSync,
    isError: isPaychexError,
    isPending: isPaychexPending,
    isSuccess: isPaychexSuccess,
    error: paychexError
  } = triggerPaychexPayrollSyncMutation({
    onSuccess: invalidateSyncQueries
  });
  const {
    mutate: triggerPersonioPayrollSync,
    isError: isPersonioError,
    isPending: isPersonioPending,
    isSuccess: isPersonioSuccess,
    error: personioError
  } = triggerPersonioPayrollSyncMutation({
    onSuccess: invalidateSyncQueries
  });

  const isPending = isPaychexPending || isPersonioPending;
  const isRunning = data?.processing_state === 'PROCESSING';

  const handleTrigger = () => {
    if (type === 'personio') {
      triggerPersonioPayrollSync({
        companyId: companyStore.company?.id ?? '',
        accountingPeriod: accountingPeriodString
      });
    } else {
      triggerPaychexPayrollSync({
        companyId: companyStore.company?.id ?? '',
        accountingPeriod: accountingPeriodString
      });
    }
  };

  return isLoading ? (
    <Skeleton variant="rounded" />
  ) : (
    <>
      <List
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}
      >
        {data?.events
          .sort(
            (a: Event, b: Event) => getTime(parseISO(b.timestamp)) - getTime(parseISO(a.timestamp))
          )
          .map((e: Event) => {
            const isError =
              e.message.includes('Failed') || e.message.includes('not_found_employees');
            return (
              <ListItem
                sx={{
                  bgcolor: isError ? 'error.light' : 'white',
                  borderRadius: '0.25rem'
                }}
                key={e.id}
              >
                {formatRelative(parseISO(e.timestamp), new Date(), {
                  locale: i18n.language === 'en' ? enUS : de
                })}
                {' - '}
                {e.message}
              </ListItem>
            );
          })}
      </List>
      <>
        <Button
          variant="contained"
          color="primary"
          onClick={handleTrigger}
          disabled={isPending || isRunning}
          sx={{ mt: 2 }}
        >
          {isRunning ? t('tasks:SYNC_RUNNING') : t('tasks:SYNC_RESTART')}
        </Button>
        {(isPaychexError || isPersonioError) && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {t(resolveSyncErrorKey(paychexError ?? personioError))}
          </Alert>
        )}
        {(isPaychexSuccess || isPersonioSuccess) && (
          <Alert severity="success" sx={{ mt: 1 }}>
            {t('tasks:SYNC_STARTED')}
          </Alert>
        )}
      </>
    </>
  );
}

interface SyncLogsProps {
  type: SyncType;
}

export function SyncLogs({ type }: Readonly<SyncLogsProps>) {
  const { companyStore } = useStore();
  const { t, i18n } = useTranslation();
  const {
    data: logs,
    isFetching,
    isRefetching
  } = type === 'personio'
    ? listPersonioSyncLogsQuery(companyStore.company?.id ?? '')
    : listPaychexPayrollSyncsQuery(companyStore.company?.id ?? '');
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const columnHelper = createColumnHelper<SyncLog>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('accounting_period', {
        header: t('export:ACCOUNTING_PERIOD'),
        cell: ({ getValue }) =>
          format(Util.getDate(getValue() ?? undefined), 'MMMM yyyy', {
            locale: i18n.language === 'en' ? enUS : de
          })
      }),
      columnHelper.accessor('created_at', {
        header: t('tasks:CREATE_DATE'),
        cell: ({ getValue }) =>
          formatRelative(new Date(getValue()), new Date(), {
            locale: i18n.language === 'en' ? enUS : de
          })
      }),
      columnHelper.accessor('processing_state', {
        header: t('tasks:STATUS'),
        cell: ({ getValue }) => {
          const state = getValue() ?? 'CREATED';
          return (
            <StatusBadge
              mode={SYNC_STATE_MODE[state]}
              label={t(`tasks:SYNC_STATE_${state}`)}
              hideIcon
              notFilled
            />
          );
        }
      }),
      columnHelper.display({
        id: 'expand',
        cell: ({ row }) =>
          row.getCanExpand() ? (
            row.getIsExpanded() ? (
              <ArrowUpIcon />
            ) : (
              <ArrowRightIcon />
            )
          ) : (
            <ArrowRightIcon />
          )
      })
    ],
    [i18n.language]
  );

  const table = useReactTable({
    data: logs ?? [],
    columns: columns,
    state: {
      expanded
    },
    getRowId: (original) => original.id,
    onExpandedChange: setExpanded,
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel<SyncLog>(),
    getExpandedRowModel: getExpandedRowModel()
  });

  return (
    <TableContainer>
      <Table>
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableCell key={header.id}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {isFetching || isRefetching ? (
            <TableRow>
              <TableCell sx={{ padding: 0, height: 'unset' }} colSpan={columns.length}>
                <LinearProgress />
              </TableCell>
            </TableRow>
          ) : null}
        </TableHead>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <Fragment key={row.id}>
              <TableRow
                sx={{
                  '&:hover': { bgcolor: 'grey.100', cursor: 'pointer' },
                  bgcolor: row.getIsExpanded() ? 'grey.200' : 'none'
                }}
                onClick={row.getToggleExpandedHandler()}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>

              <TableRow
                sx={{ display: row.getIsExpanded() ? 'table-row' : 'none', bgcolor: 'grey.200' }}
              >
                <TableCell colSpan={row.getVisibleCells().length}>
                  <EventLogsComponent row={row} type={type} />
                </TableCell>
              </TableRow>
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
